<template>
  <div class="flex items-center justify-center p-0 w-full"
      @dragenter.prevent="onDragEnter"
      @dragleave.prevent="onDragLeave"
      @dragover.prevent="onDragOver"
      @drop.prevent="onDrop">
    <!-- Drag Overlay -->
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div 
        v-if="isDragging"
        class="fixed inset-0 z-[100] bg-[#2EB2A0]/95 backdrop-blur-sm flex flex-col items-center justify-center pointer-events-none"
      >
        <div class="bg-white/20 p-8 rounded-full mb-8 backdrop-blur-sm shadow-2xl">
           <span class="material-symbols-outlined text-white text-[100px]">compress</span>
        </div>
        <h2 class="text-white text-5xl font-bold tracking-tight drop-shadow-sm">Drop PDF file to Compress</h2>
      </div>
    </transition>

    <!-- Dropzone -->
    <div 
      v-if="pages.length === 0" 
      @click="fileInput.click()" 
      class="w-full max-w-[1000px] mt-4 transition-all duration-300 cursor-pointer flex flex-col items-center justify-center text-center relative group p-0"
    >
      <div class="absolute -inset-1 bg-gradient-to-r from-[#2EB2A0] to-[#2EB2A0]/60 rounded-2xl blur opacity-10 group-hover:opacity-25 transition duration-1000 group-hover:duration-200"></div>
      <div class="relative w-full h-[360px] bg-white border-2 border-dashed border-slate-300 rounded-2xl flex flex-col items-center justify-center gap-6 transition-all hover:border-[#2EB2A0]/50 p-6">
        <div class="size-20 bg-[#2EB2A0]/10 rounded-full flex items-center justify-center text-[#2EB2A0] mt-2">
           <span class="material-symbols-outlined text-[48px]">compress</span>
        </div>
        
        <div class="space-y-1">
            <h3 class="text-2xl font-bold text-[#16304D]">Select or Drop PDF File to Compress</h3>
            <p class="text-sm font-medium text-slate-500">Reduce PDF file size while maintaining excellent document quality</p>
        </div>

        <button @click.stop="fileInput.click()" class="bg-[#2EB2A0] text-white text-base font-semibold px-8 py-3 rounded-xl hover:bg-[#279B8C] shadow-xl shadow-[#2EB2A0]/20 transition-all duration-300 ease-in-out transform hover:-translate-y-0.5 flex items-center gap-2">
            <span class="material-symbols-outlined">add_circle</span>
            <span>Select PDF File</span>
        </button>

        <div class="flex flex-wrap justify-center items-center gap-4 text-[10px] font-semibold text-slate-400 uppercase tracking-widest mt-1">
             <span class="flex items-center gap-1.5"><span class="material-symbols-outlined text-[14px]">lock</span> Encrypted &amp; Secure</span>
             <span class="flex items-center gap-1.5"><span class="material-symbols-outlined text-[14px]">tune</span> 3 Compression Profiles</span>
             <span class="flex items-center gap-1.5"><span class="material-symbols-outlined text-[14px]">percent</span> Up to 80% Smaller</span>
        </div>
      </div>
    </div>

    <!-- Main Compress Workspace -->
    <div v-else class="fixed inset-0 top-0 z-50 flex flex-col h-screen bg-[#F3F4F6]">
        <!-- Top Navigation Bar -->
        <header class="flex items-center justify-between whitespace-nowrap border-b border-slate-200 bg-white px-4 md:px-6 py-3 shrink-0 shadow-sm z-20">
            <div class="flex items-center gap-3 md:gap-4">
                <a href="/" class="flex items-center gap-2">
                     <img :src="'/logo.png'" alt="Logo" class="h-8 md:h-10 w-auto" @error="(e) => e.target.style.display='none'">
                     <span class="font-bold text-[#16304D] text-lg hidden sm:inline">Compress PDF</span>
                </a>
                <div class="h-6 w-px bg-slate-200 hidden sm:block"></div>
                <div>
                    <div class="flex items-center gap-2 text-xs md:text-sm text-slate-600 font-medium">
                        <span class="font-bold text-[#16304D] truncate max-w-[200px]">{{ currentFileName }}</span>
                        <span class="text-slate-300">&middot;</span>
                        <span class="font-bold text-[#2EB2A0]">{{ formatSize(originalSizeBytes) }} Original Size</span>
                    </div>
                </div>
            </div>
            <div class="flex items-center gap-2 md:gap-4">
                <button @click="resetAll" class="flex items-center justify-center gap-2 rounded-xl h-10 px-4 bg-red-50 border border-red-200 text-sm font-semibold hover:bg-red-100 text-red-600 transition-colors">
                    <span class="material-symbols-outlined text-lg">restart_alt</span>
                    <span class="hidden md:inline">Start Over</span>
                </button>
            </div>
        </header>

        <div class="flex flex-1 overflow-hidden relative">
            <!-- Main Preview & Compression Settings -->
            <main class="flex-1 overflow-y-auto bg-[#F3F4F6] p-4 md:p-8 relative">
                <div class="max-w-4xl mx-auto pb-24 space-y-6">

                    <!-- Savings Dashboard Banner -->
                    <div class="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                        <div class="p-4 bg-slate-50 rounded-xl border border-slate-100">
                            <span class="text-xs font-bold text-slate-400 uppercase tracking-wider">Original Size</span>
                            <h4 class="text-2xl font-bold text-[#16304D] mt-1">{{ formatSize(originalSizeBytes) }}</h4>
                        </div>
                        <div class="p-4 bg-[#2EB2A0]/10 rounded-xl border border-[#2EB2A0]/30">
                            <span class="text-xs font-bold text-[#2EB2A0] uppercase tracking-wider">Estimated New Size</span>
                            <h4 class="text-2xl font-bold text-[#2EB2A0] mt-1">{{ formatSize(estimatedSizeBytes) }}</h4>
                        </div>
                        <div class="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                            <span class="text-xs font-bold text-emerald-600 uppercase tracking-wider">Space Saved</span>
                            <h4 class="text-2xl font-bold text-emerald-600 mt-1">~{{ estimatedReductionPercent }}% Off</h4>
                        </div>
                    </div>

                    <!-- Compression Level Selection -->
                    <div class="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                        <h3 class="text-lg font-bold text-[#16304D] mb-4 flex items-center gap-2">
                             <span class="material-symbols-outlined text-[#2EB2A0]">tune</span>
                             Select Compression Level
                        </h3>

                        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                             <!-- Recommended -->
                             <div 
                                @click="compressionLevel = 'recommended'"
                                class="p-5 rounded-2xl border-2 cursor-pointer transition-all relative overflow-hidden flex flex-col justify-between"
                                :class="compressionLevel === 'recommended' ? 'border-[#2EB2A0] bg-[#2EB2A0]/5 shadow-md' : 'border-slate-200 hover:border-slate-300 bg-white'"
                             >
                                 <div class="flex justify-between items-start mb-3">
                                     <span class="text-xs font-bold px-2.5 py-1 rounded-full bg-[#2EB2A0] text-white">RECOMMENDED</span>
                                     <span class="material-symbols-outlined" :class="compressionLevel === 'recommended' ? 'text-[#2EB2A0]' : 'text-slate-300'">check_circle</span>
                                 </div>
                                 <div>
                                     <h4 class="font-bold text-slate-900 text-base">Good Compression</h4>
                                     <p class="text-xs text-slate-500 mt-1">Good quality &amp; file size balance (~50% reduction)</p>
                                 </div>
                             </div>

                             <!-- Extreme -->
                             <div 
                                @click="compressionLevel = 'extreme'"
                                class="p-5 rounded-2xl border-2 cursor-pointer transition-all relative overflow-hidden flex flex-col justify-between"
                                :class="compressionLevel === 'extreme' ? 'border-[#2EB2A0] bg-[#2EB2A0]/5 shadow-md' : 'border-slate-200 hover:border-slate-300 bg-white'"
                             >
                                 <div class="flex justify-between items-start mb-3">
                                     <span class="text-xs font-bold px-2.5 py-1 rounded-full bg-amber-500 text-white">MAX SAVINGS</span>
                                     <span class="material-symbols-outlined" :class="compressionLevel === 'extreme' ? 'text-[#2EB2A0]' : 'text-slate-300'">check_circle</span>
                                 </div>
                                 <div>
                                     <h4 class="font-bold text-slate-900 text-base">Extreme Compression</h4>
                                     <p class="text-xs text-slate-500 mt-1">Highest size reduction (~75% reduction)</p>
                                 </div>
                             </div>

                             <!-- Less -->
                             <div 
                                @click="compressionLevel = 'less'"
                                class="p-5 rounded-2xl border-2 cursor-pointer transition-all relative overflow-hidden flex flex-col justify-between"
                                :class="compressionLevel === 'less' ? 'border-[#2EB2A0] bg-[#2EB2A0]/5 shadow-md' : 'border-slate-200 hover:border-slate-300 bg-white'"
                             >
                                 <div class="flex justify-between items-start mb-3">
                                     <span class="text-xs font-bold px-2.5 py-1 rounded-full bg-slate-700 text-white">HIGH QUALITY</span>
                                     <span class="material-symbols-outlined" :class="compressionLevel === 'less' ? 'text-[#2EB2A0]' : 'text-slate-300'">check_circle</span>
                                 </div>
                                 <div>
                                     <h4 class="font-bold text-slate-900 text-base">Less Compression</h4>
                                     <p class="text-xs text-slate-500 mt-1">Highest visual clarity (~25% reduction)</p>
                                 </div>
                             </div>
                        </div>
                    </div>

                    <!-- Page Preview Grid -->
                    <div class="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                        <h3 class="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4">Document Preview ({{ pages.length }} Pages)</h3>
                        <div class="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-4">
                             <div v-for="page in pages" :key="page.uid" class="h-36 bg-slate-50 rounded-lg border border-slate-200 overflow-hidden relative flex items-center justify-center p-2">
                                  <img v-show="page.thumbnail" :src="page.thumbnail" class="max-h-full max-w-full object-contain" />
                                  <span class="absolute bottom-1 right-2 text-[10px] font-bold text-slate-400">P. {{ page.pageNum }}</span>
                             </div>
                        </div>
                    </div>
                </div>
            </main>

            <!-- Bottom Action Footer -->
            <div class="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 shadow-xl z-40 flex items-center justify-between max-w-4xl mx-auto rounded-t-2xl">
                <div>
                     <span class="text-xs font-bold text-slate-400 uppercase">Selected Profile</span>
                     <p class="text-sm font-bold text-[#16304D] capitalize">{{ compressionLevel }} Compression</p>
                </div>
                <button @click="compressAndDownload" :disabled="isCompressing" class="bg-[#2EB2A0] text-white px-8 h-12 rounded-xl text-base font-bold flex items-center justify-center gap-2 shadow-lg hover:bg-[#279B8C] transition-all disabled:opacity-50">
                    <span v-if="isCompressing" class="animate-spin material-symbols-outlined">progress_activity</span>
                    <span v-else class="material-symbols-outlined">compress</span>
                    <span>{{ isCompressing ? 'Compressing PDF...' : 'Compress & Download PDF' }}</span>
                </button>
            </div>
        </div>
    </div>

    <!-- Hidden Input -->
    <input type="file" ref="fileInput" accept="application/pdf" class="hidden" @change="onFileChange" />
  </div>
</template>

<script setup>
import { ref, computed, nextTick, shallowRef, markRaw } from 'vue'
import { PDFDocument } from 'pdf-lib'
import * as pdfjsLib from 'pdfjs-dist/build/pdf'

pdfjsLib.GlobalWorkerOptions.workerSrc = '/js/pdf.worker.js';

const pages = ref([])
const pdfjsDoc = shallowRef(null)
const originalFileBuffer = ref(null)
const currentFileName = ref('')
const originalSizeBytes = ref(0)
const isDragging = ref(false)
const isCompressing = ref(false)
const compressionLevel = ref('recommended')
const fileInput = ref(null)

const estimatedReductionPercent = computed(() => {
  if (compressionLevel.value === 'extreme') return 75
  if (compressionLevel.value === 'less') return 25
  return 50
})

const estimatedSizeBytes = computed(() => {
  return Math.round(originalSizeBytes.value * (1 - estimatedReductionPercent.value / 100))
})

const formatSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 KB'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

let uidCounter = 0

const processFile = async (file) => {
  if (file.type !== 'application/pdf') return
  currentFileName.value = file.name
  originalSizeBytes.value = file.size
  originalFileBuffer.value = await file.arrayBuffer()
  
  try {
    const uint8 = new Uint8Array(originalFileBuffer.value.slice(0))
    const loadedPdfjs = await pdfjsLib.getDocument({ data: uint8 }).promise
    pdfjsDoc.value = markRaw(loadedPdfjs)
  } catch (e) {
    console.error('PDFJS load error:', e)
  }

  const totalP = pdfjsDoc.value ? pdfjsDoc.value.numPages : 1
  const newPages = []
  for (let i = 1; i <= totalP; i++) {
    newPages.push({
      uid: uidCounter++,
      pageNum: i,
      thumbnail: '',
      isLoading: true
    })
  }

  pages.value = newPages
  renderThumbnailsBackground()
}

const renderThumbnailsBackground = async () => {
  for (const pageObj of pages.value) {
    try {
      await new Promise(resolve => setTimeout(resolve, 30))
      if (pdfjsDoc.value) {
        const pdfPage = await pdfjsDoc.value.getPage(pageObj.pageNum)
        const viewport = pdfPage.getViewport({ scale: 1.0, rotation: 0 })
        const canvas = document.createElement('canvas')
        canvas.width = viewport.width
        canvas.height = viewport.height
        const ctx = canvas.getContext('2d')
        await pdfPage.render({ canvasContext: ctx, viewport }).promise
        pageObj.thumbnail = canvas.toDataURL('image/png')
      }
      pageObj.isLoading = false
    } catch (e) {
      pageObj.isLoading = false
    }
  }
}

const compressAndDownload = async () => {
  try {
    isCompressing.value = true
    await nextTick()

    let renderScale = 1.5
    let jpegQuality = 0.6

    if (compressionLevel.value === 'extreme') {
      renderScale = 1.0
      jpegQuality = 0.45
    } else if (compressionLevel.value === 'less') {
      renderScale = 2.0
      jpegQuality = 0.85
    }

    const compressedPdf = await PDFDocument.create()

    for (let i = 1; i <= pdfjsDoc.value.numPages; i++) {
      const pdfPage = await pdfjsDoc.value.getPage(i)
      const viewport = pdfPage.getViewport({ scale: renderScale })
      
      const canvas = document.createElement('canvas')
      canvas.width = viewport.width
      canvas.height = viewport.height
      const ctx = canvas.getContext('2d')
      
      await pdfPage.render({ canvasContext: ctx, viewport }).promise
      
      const imgDataUrl = canvas.toDataURL('image/jpeg', jpegQuality)
      const jpegImageBytes = await fetch(imgDataUrl).then(res => res.arrayBuffer())
      
      const embeddedImage = await compressedPdf.embedJpg(jpegImageBytes)
      const newPage = compressedPdf.addPage([embeddedImage.width, embeddedImage.height])
      newPage.drawImage(embeddedImage, {
        x: 0,
        y: 0,
        width: embeddedImage.width,
        height: embeddedImage.height
      })
    }

    const bytes = await compressedPdf.save()
    const blob = new Blob([bytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `compressed-${currentFileName.value || 'document.pdf'}`
    a.click()
    URL.revokeObjectURL(url)
  } catch (e) {
    console.error(e)
    alert('Error compressing PDF: ' + e.message)
  } finally {
    isCompressing.value = false
  }
}

const onFileChange = (e) => {
  if (e.target.files.length) processFile(e.target.files[0])
  e.target.value = ''
}

const resetAll = () => {
  pages.value = []
  pdfjsDoc.value = null
  originalFileBuffer.value = null
  currentFileName.value = ''
  originalSizeBytes.value = 0
}

const onDragEnter = (e) => { e.preventDefault(); isDragging.value = true; }
const onDragLeave = (e) => { e.preventDefault(); isDragging.value = false; }
const onDragOver = (e) => { e.preventDefault(); }
const onDrop = (e) => {
  e.preventDefault()
  isDragging.value = false
  if (e.dataTransfer.files.length) processFile(e.dataTransfer.files[0])
}
</script>
